/**
 * Cryptographically secure random integer between min (inclusive) and max (inclusive)
 */
export function secureRandomInt(min: number, max: number): number {
  if (min > max) {
    throw new Error(`min (${min}) must be <= max (${max})`);
  }
  if (min === max) return min;

  const range = max - min + 1;
  const maxAllowed = Math.floor(0xffffffff / range) * range;
  const randomBuffer = new Uint32Array(1);

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    let randomValue: number;
    do {
      crypto.getRandomValues(randomBuffer);
      randomValue = randomBuffer[0];
    } while (randomValue >= maxAllowed);

    return min + (randomValue % range);
  }

  // Fallback to Math.random if crypto is somehow unavailable
  return min + Math.floor(Math.random() * range);
}

/**
 * Cryptographically secure float between 0 (inclusive) and 1 (exclusive)
 */
export function secureRandomFloat(): number {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const randomBuffer = new Uint32Array(1);
    crypto.getRandomValues(randomBuffer);
    return randomBuffer[0] / (0xffffffff + 1);
  }
  return Math.random();
}

/**
 * Pick a random item from an array using cryptographic randomness
 */
export function secureRandomChoice<T>(items: T[]): { item: T; index: number } {
  if (items.length === 0) {
    throw new Error('Cannot pick from an empty array');
  }
  const index = secureRandomInt(0, items.length - 1);
  return { item: items[index], index };
}

/**
 * Shuffle array in place with Fisher-Yates using crypto randomness
 */
export function secureShuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = secureRandomInt(0, i);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Generate a unique ID (UUID v4 or random hex)
 */
export function generateUniqueId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'id_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now().toString(36);
}
